// ABOUTME: Initiative dialog for submitting platform improvement ideas
// ABOUTME: Pre-selects 'initiative' type which is read-only in the form
import { zodResolver } from "@hookform/resolvers/zod";
import { Lightbulb } from "lucide-react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/shared/ui/kit/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/kit/form";
import { Input } from "@/shared/ui/kit/input";
import { Textarea } from "@/shared/ui/kit/textarea";
import { ResponsiveModal } from "@/shared/ui/responsive-modal";

import { useCreateTicket } from "../model/use-create-ticket";

interface InitiativeDialogProps {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  onSuccess?: (ticketNumber: string) => void;
}

export function InitiativeDialog({
  isOpen,
  onOpenChange,
  onSuccess,
}: InitiativeDialogProps) {
  const { t } = useTranslation();
  const { mutate: createTicket, isPending } = useCreateTicket();

  const formSchema = useMemo(
    () =>
      z.object({
        subject: z
          .string()
          .min(3, t("tickets.validation.subjectMin"))
          .max(100, t("tickets.validation.subjectMax")),
        message: z
          .string()
          .min(10, t("tickets.validation.messageMin"))
          .max(2000, t("tickets.validation.messageMax")),
      }),
    [t],
  );

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      subject: "",
      message: "",
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    createTicket(
      {
        body: {
          ticket: {
            subject: data.subject.trim(),
            category: "other",
            message: data.message.trim(),
            ticket_type: "initiative",
          },
        },
      },
      {
        onSuccess: (data) => {
          const ticketId = data.id;
          toast.success(t("tickets.initiative.success"));
          if (ticketId) {
            onSuccess?.(ticketId);
          }
          form.reset();
          onOpenChange(false);
        },
        onError: (error) => {
          const message =
            error && typeof error === "object" && "error" in error
              ? (error.error as { message?: string })?.message
              : t("tickets.error.createFailed");
          toast.error(message || t("tickets.error.createFailed"));
        },
      },
    );
  });

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPending) {
      form.reset();
    }
    onOpenChange(open);
  };

  return (
    <ResponsiveModal open={isOpen} onOpenChange={handleOpenChange}>
      <ResponsiveModal.Content>
        <ResponsiveModal.Header>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-500/10">
              <Lightbulb className="h-4 w-4 text-amber-500" />
            </div>
            <div className="flex flex-col gap-1">
              <ResponsiveModal.Title>
                {t("tickets.initiative.title")}
              </ResponsiveModal.Title>
              <ResponsiveModal.Description>
                {t("tickets.initiative.description")}
              </ResponsiveModal.Description>
            </div>
          </div>
        </ResponsiveModal.Header>

        <ResponsiveModal.Body>
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-3">
              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">
                      {t("tickets.createTicket.subject")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("tickets.initiative.subjectPlaceholder")}
                        disabled={isPending}
                        maxLength={100}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">
                      {t("tickets.createTicket.message")}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={t("tickets.initiative.messagePlaceholder")}
                        disabled={isPending}
                        rows={4}
                        maxLength={2000}
                        className="resize-none"
                      />
                    </FormControl>
                    <div className="flex justify-between">
                      <FormMessage />
                      <p className="text-xs text-muted-foreground ml-auto">
                        {field.value?.length || 0}/2000
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </ResponsiveModal.Body>

        <ResponsiveModal.Footer>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
              className="flex-1 h-10"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit()}
              disabled={!form.formState.isValid || isPending}
              className="flex-1 h-10"
            >
              {isPending
                ? t("tickets.createTicket.sending")
                : t("tickets.createTicket.submit")}
            </Button>
          </div>
        </ResponsiveModal.Footer>
      </ResponsiveModal.Content>
    </ResponsiveModal>
  );
}
